import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useGetCrmClientQuery, useListAgenticWmThreadsQuery, useListCrmTasksQuery } from '../../app/api';
import { useIsMobile, gemstoneInfo } from '../utils';
import FscDocumentTree from './FscDocumentTree';
import { GemBadge, ProductPills, PRODUCTS } from './Crm';
import useRoleJSON from './useRoleJSON';
import { useTaskLookups, TaskTable, NewTaskModal, TaskDrawer } from './TaskParts';

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
};

const fmtDate = (d) => (d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString() : null);
const fmtMoney = (n) => (n !== null && n !== undefined && n !== '' && Number(n) !== 0
  ? Number(n).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
  : null);
const crmName = (system) => (system === 'smartoffice' ? 'SmartOffice export' : 'Redtail');
// Redtail records carry a real contact id; SmartOffice ones (no id in the
// export) carry a derived key that means nothing to a person, so it is hidden.
const recordLabel = (system, recordId) => (system === 'redtail'
  ? `Redtail #${String(recordId).split(':')[1]}`
  : 'SmartOffice record');
const productLabel = (key) => PRODUCTS.find((p) => p.key === key)?.label || 'Other';
// Where a non-account product row came from.
const ORIGIN_NOTES = { keyword: 'keyword', tax_flag: 'tax flags export' };

function Card({ title, icon, children, right }) {
  return (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.6rem 0.9rem', borderBottom: `1px solid ${work.hairline}` }}>
        <i className={`bi bi-${icon}`} style={{ color: work.goldenrod }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.ink }}>{title}</span>
        {right && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: work.muted }}>{right}</span>}
      </div>
      <div style={{ padding: '0.75rem 0.9rem' }}>{children}</div>
    </div>
  );
}
Card.propTypes = { title: PropTypes.string, icon: PropTypes.string, children: PropTypes.node, right: PropTypes.node };

function Field({ label, children }) {
  const empty = children === null || children === undefined || children === '';
  return (
    <div style={{ display: 'flex', gap: '0.75rem', padding: '0.3rem 0', borderBottom: `1px dashed ${work.hairline}` }}>
      <div style={{ flex: '0 0 130px', color: work.muted, fontSize: '0.78rem' }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, color: empty ? work.muted : work.ink, fontSize: '0.86rem', overflowWrap: 'anywhere' }}>
        {empty ? '-' : children}
      </div>
    </div>
  );
}
Field.propTypes = { label: PropTypes.string, children: PropTypes.node };

/**
 * One CRM client: everything the source CRM knows, plus how it was folded
 * together (duplicate records) and whether it is linked to Salesforce yet.
 * Read-only - corrections are made in the firm's CRM and picked up on the
 * next pull.
 */
export default function CrmClientDetail() {
  const { clientId } = useParams();
  const isMobile = useIsMobile();
  const { data: c, isLoading, isError } = useGetCrmClientQuery({ id: clientId });
  // Agentic WM is still in beta, so the header button (and the query behind
  // its count) is only there for BETA holders.
  const roleJSON = useRoleJSON();
  const showAgenticWm = Boolean(roleJSON?.isBeta);
  // Tasks is in beta too, and has its own area role.
  const showTasks = Boolean(roleJSON?.isBeta && roleJSON?.isWorkTasks);
  // The caller's own Agentic WM conversations about this client, for the count
  // on the header button.
  const { data: agenticThreads } = useListAgenticWmThreadsQuery({ client_id: clientId }, { skip: !showAgenticWm });
  const [showUnclassified, setShowUnclassified] = useState(false);

  // The list's folder icon links to #documents; jump there once the record has
  // rendered (the browser's own anchor scroll fires before the data arrives).
  useEffect(() => {
    if (c && window.location.hash === '#documents') {
      document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [c]);

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="spinner-border" style={{ color: work.tealBlue }} /></div>;
  }
  if (isError || !c) {
    return (
      <div style={{ padding: '2rem' }}>
        <Link to="/work/crm">&larr; Back to CRM</Link>
        <p style={{ marginTop: '1rem', color: work.muted }}>This client was not found, or is outside the offices you can see.</p>
      </div>
    );
  }

  const hub = gemstoneInfo(c.gemstone);
  // Untyped "Other" accounts with no carrier or plan (AWM has up to 85 on one
  // client from an account feed) collapse into one row so they cannot bury
  // the products that say something.
  const isUnclassified = (p) => p.origin === 'account' && p.product_category === 'other';
  const classified = c.products.filter((p) => !isUnclassified(p));
  const unclassified = c.products.filter(isUnclassified);
  const unclassifiedBalance = unclassified.reduce((sum, p) => sum + (Number(p.balance) || 0), 0);
  const openProducts = classified.filter((p) => p.is_open);
  const closedProducts = classified.filter((p) => !p.is_open);
  const multi = c.group.length > 1;

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <Link to="/work/crm" style={{ color: work.tealBlue, fontSize: '0.85rem', textDecoration: 'none' }}>&larr; CRM</Link>

      {/* Header */}
      <div style={{ background: work.deepBlue, borderBottom: `3px solid ${work.goldenrod}`, borderRadius: 12, padding: '1rem 1.25rem', margin: '0.6rem 0 1rem', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0, fontWeight: 700 }}>{c.display_name}</h4>
          {c.status && (
            <span style={{ background: c.is_client ? 'rgba(26,127,78,0.9)' : 'rgba(255,255,255,0.15)', borderRadius: 4, padding: '0.1rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
              {c.status}
            </span>
          )}
          {c.gemstone_source && (
            <span style={{ background: 'white', borderRadius: 4, padding: '0.1rem 0.5rem' }}>
              <GemBadge value={c.gemstone_source} gemstone={c.gemstone} />
            </span>
          )}
          {showAgenticWm && (
            <Link to={`/work/agentic-wm?client=${c.id}`} className="btn btn-sm"
                  style={{ marginLeft: 'auto', background: work.goldenrod, color: 'white', fontWeight: 600 }}>
              <i className="bi bi-stars me-1" />Agentic WM{agenticThreads?.threads?.length ? ` (${agenticThreads.threads.length})` : ''}
            </Link>
          )}
        </div>
        <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)' }}>
          {[c.household_name && `Household: ${c.household_name}`, c.source_name, c.contact_type].filter(Boolean).join(' · ')}
        </div>
        <div style={{ marginTop: 10 }}><ProductPillsOnDark row={c} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))', gap: '1rem' }}>
        <Card title="Relationship" icon="person-badge">
          <Field label="Advisor">{c.advisor_name}</Field>
          <Field label="Second advisor">{c.advisor_name_2}</Field>
          <Field label="Tax preparer">{c.tax_preparer}</Field>
          <Field label="Gemstone">
            {c.gemstone_source && (
              <>
                {c.gemstone_source}
                <span style={{ color: work.muted, fontSize: '0.78rem' }}>
                  {hub ? ` (hub tier ${hub.label})` : ' (not mapped to a hub tier yet)'}
                </span>
              </>
            )}
          </Field>
          <Field label="Client since">{fmtDate(c.client_since)}</Field>
          <Field label="Lead source">{c.source}</Field>
          <Field label="Referred by">{c.referred_by}</Field>
          <Field label="Keywords">{c.keywords}</Field>
        </Card>

        <Card title="Contact" icon="person-vcard">
          {c.phones.length === 0 && <Field label="Phone">{c.phone}</Field>}
          {c.phones.map((p, i) => (
            <Field key={`p${i}`} label={`${p.type || 'Phone'}${p.is_primary ? ' (primary)' : ''}`}>{p.number}</Field>
          ))}
          {c.emails.length === 0 && <Field label="Email">{c.email}</Field>}
          {c.emails.map((e, i) => (
            <Field key={`e${i}`} label={`${e.type || 'Email'}${e.is_primary ? ' (primary)' : ''}`}>
              {e.address && <a href={`mailto:${e.address}`} style={{ color: work.tealBlue }}>{e.address}</a>}
            </Field>
          ))}
          {c.addresses.length === 0 && <Field label="Address">{[c.city, c.state, c.postal].filter(Boolean).join(', ')}</Field>}
          {c.addresses.map((a, i) => (
            <Field key={`a${i}`} label={`${a.type || 'Address'}${a.is_primary ? ' (primary)' : ''}`}>
              {a.street && <div>{a.street}</div>}
              <div>{[a.city, a.state].filter(Boolean).join(', ')} {a.zip}</div>
            </Field>
          ))}
          <Field label="Date of birth">{fmtDate(c.dob)}</Field>
          <Field label="Spouse">{c.spouse_name}</Field>
          <Field label="Employer">{[c.job_title, c.employer || c.company_name].filter(Boolean).join(', ')}</Field>
        </Card>
      </div>

      {showTasks && (
        <div style={{ marginTop: '1rem' }}>
          <ClientTasks client={{ id: c.id, name: c.display_name }} isMobile={isMobile} />
        </div>
      )}

      <div style={{ marginTop: '1rem' }}>
        <Card
          title="Products"
          icon="briefcase"
          right={`${openProducts.length} open${closedProducts.length ? ` · ${closedProducts.length} closed` : ''}${unclassified.length ? ` · ${unclassified.length} unclassified` : ''}`}
        >
          {c.products.length === 0 ? (
            <p style={{ margin: 0, color: work.muted, fontSize: '0.86rem' }}>No accounts or product keywords on record in the source CRM.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.84rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th>Product</th><th>Type</th><th>Carrier / custodian</th><th>Plan</th><th>Status</th>
                    <th className="text-end">Balance</th><th>Acct</th>{multi && <th>Record</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...openProducts, ...closedProducts, ...(showUnclassified ? unclassified : [])].map((p) => (
                    <tr key={p.id} style={{ opacity: p.is_open ? 1 : 0.55 }}>
                      <td style={{ fontWeight: 700, color: work.ink, whiteSpace: 'nowrap' }}>{productLabel(p.product_category)}</td>
                      <td>
                        {p.label}
                        {p.origin !== 'account' && <span style={{ marginLeft: 4, fontSize: '0.68rem', color: work.muted }}>({ORIGIN_NOTES[p.origin] || 'client category'})</span>}
                      </td>
                      <td>{p.company || '-'}</td>
                      {/* A tax flag's `product` is its method key, not a plan name. */}
                      <td>{(p.origin !== 'tax_flag' && p.product) || '-'}</td>
                      <td>{p.status || (p.is_open ? 'Open' : 'Closed')}</td>
                      <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.balance) || '-'}</td>
                      <td style={{ color: work.muted }}>{p.number_last4 ? `...${p.number_last4}` : '-'}</td>
                      {multi && <td style={{ color: work.muted }}>{p.held_by}</td>}
                    </tr>
                  ))}
                  {unclassified.length > 0 && (
                    <tr>
                      <td colSpan={multi ? 8 : 7} style={{ color: work.muted, fontSize: '0.8rem' }}>
                        {unclassified.length} account{unclassified.length === 1 ? '' : 's'} with no type, carrier or plan in the source CRM
                        {unclassifiedBalance ? ` (${fmtMoney(unclassifiedBalance)} total)` : ''}
                        {' - '}
                        <button
                          type="button"
                          onClick={() => setShowUnclassified((v) => !v)}
                          style={{ background: 'none', border: 'none', padding: 0, color: work.tealBlue, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                        >
                          {showUnclassified ? 'hide' : 'show'}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))', gap: '1rem', marginTop: '1rem' }}>
        <Card title="Household" icon="house-heart" right={c.household_name}>
          {c.household.length === 0 ? (
            <p style={{ margin: 0, color: work.muted, fontSize: '0.86rem' }}>Not in a household in the source CRM.</p>
          ) : c.household.map((h) => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderBottom: `1px dashed ${work.hairline}` }}>
              {h.id === (c.dup_group || c.id) ? (
                <span style={{ fontWeight: 700, color: work.ink }}>{h.display_name} <span style={{ color: work.muted, fontWeight: 400, fontSize: '0.75rem' }}>(this client)</span></span>
              ) : (
                <Link to={`/work/crm/${h.id}`} style={{ fontWeight: 700, color: work.tealBlue, textDecoration: 'none' }}>{h.display_name}</Link>
              )}
              <span style={{ color: work.muted, fontSize: '0.78rem' }}>{h.status}</span>
              <span style={{ marginLeft: 'auto' }}><ProductPills row={h} /></span>
            </div>
          ))}
        </Card>

        <Card title="Salesforce" icon="cloud-arrow-up">
          {c.fsc_link?.linked && c.fsc_link?.fsc_household_id ? (
            <>
              <Field label="FSC household">{c.fsc_link.fsc_household_name || c.fsc_link.fsc_household_id}</Field>
              <Field label="Matched on">{c.fsc_link.matched_on}</Field>
              <Field label="Orion AUM">{fmtMoney(c.fsc_link.current_aum)}</Field>
            </>
          ) : c.fsc_link?.fsc_household_id ? (
            <>
              <p style={{ marginTop: 0, color: work.goldenrod, fontSize: '0.86rem', fontWeight: 700 }}>
                <i className="bi bi-question-circle" /> Possible match - needs review
              </p>
              <Field label="FSC household">{c.fsc_link.fsc_household_name || c.fsc_link.fsc_household_id}</Field>
              <Field label="Matched on">{`${c.fsc_link.matched_on || '-'} (${c.fsc_link.confidence} confidence)`}</Field>
              <p style={{ margin: '0.5rem 0 0', color: work.muted, fontSize: '0.78rem' }}>
                Not counted as linked, and no AUM is shown, until a person confirms it.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, color: work.muted, fontSize: '0.86rem' }}>
              Not linked to an FSC household yet. Links are made by the identity matching run:
              an SSN or email match links automatically; a date of birth + ZIP or name + ZIP
              match waits for a person to confirm it.
            </p>
          )}
        </Card>
      </div>

      {/* Documents come through the Salesforce link: the hub's document store is
          keyed by FSC household, so only a CONFIRMED link shows them - a
          possible match never does. Same tree, office check and access log as
          the Salesforce Clients page. */}
      <div id="documents" style={{ marginTop: '1rem', scrollMarginTop: '1rem' }}>
        {c.fsc_link?.linked && c.fsc_link?.fsc_household_id ? (
          <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.6rem 0.9rem', borderBottom: `1px solid ${work.hairline}` }}>
              <i className="bi bi-folder2-open" style={{ color: work.goldenrod }} />
              <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.ink }}>Documents</span>
              <Link
                to={`/work/clients/fsc/${c.fsc_link.fsc_household_id}`}
                style={{ marginLeft: 'auto', fontSize: '0.75rem', color: work.tealBlue, textDecoration: 'none' }}
              >
                Open the Salesforce household <i className="bi bi-box-arrow-up-right" />
              </Link>
            </div>
            <FscDocumentTree
              fscHouseholdId={c.fsc_link.fsc_household_id}
              isMobile={isMobile}
              emptyMessage="No documents on file for this household yet."
            />
          </div>
        ) : (
          <Card title="Documents" icon="folder2-open">
            <p style={{ margin: 0, color: work.muted, fontSize: '0.86rem' }}>
              Documents appear here once this client is linked to their Salesforce household
              (see the Salesforce card above).
            </p>
          </Card>
        )}
      </div>

      {multi && (
        <div style={{ marginTop: '1rem' }}>
          <Card title="Duplicate records" icon="files" right={`${c.group.length} records · ${c.dup_reason || 'grouped'}`}>
            <p style={{ marginTop: 0, color: work.muted, fontSize: '0.8rem' }}>
              The source CRM has more than one record for this person. They are shown here as one client;
              the first is the most complete and is the one used above. Merge them in the source CRM to clean this up.
            </p>
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.82rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th>Record</th><th>Status</th><th>Advisor</th><th>Email</th><th>Phone</th><th>Location</th><th>DOB</th><th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {c.group.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <span style={{ fontWeight: g.is_canonical ? 700 : 400 }}>{g.display_name}</span>
                        <div style={{ fontSize: '0.7rem', color: work.muted }}>
                          {recordLabel(c.source_system, g.source_record_id)}{g.is_canonical ? ' · primary' : ''}
                        </div>
                      </td>
                      <td>{g.status || '-'}</td>
                      <td>{g.advisor_name || '-'}</td>
                      <td>{g.email || '-'}</td>
                      <td>{g.phone || '-'}</td>
                      <td>{[g.city, g.state].filter(Boolean).join(', ') || '-'}</td>
                      <td>{fmtDate(g.dob) || '-'}</td>
                      <td>{fmtDate(g.source_updated_at) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <p style={{ color: work.muted, fontSize: '0.75rem', marginTop: '0.75rem', marginBottom: 0 }}>
        Read-only copy from {c.source_name}&apos;s {crmName(c.source_system)}
        {c.source_system === 'redtail' ? `, record #${String(c.source_record_id).split(':')[1]}` : ''}
        {c.source_updated_at ? `, last changed ${fmtDate(c.source_updated_at)}` : ''}.
      </p>
    </div>
  );
}

function ProductPillsOnDark({ row }) {
  const held = PRODUCTS.filter((p) => row[`has_${p.key}`]);
  if (!held.length) return <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>No products on record</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.35rem' }}>
      {held.map((p) => (
        <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 4, padding: '0.1rem 0.5rem', fontSize: '0.75rem', fontWeight: 700 }}>
          <i className={`bi bi-${p.icon}`} style={{ color: work.goldenrod }} />{p.label}
        </span>
      ))}
    </span>
  );
}
ProductPillsOnDark.propTypes = { row: PropTypes.object.isRequired };

/** This client's tasks (every record in its duplicate group), with New task. */
function ClientTasks({ client, isMobile }) {
  const { lookups } = useTaskLookups();
  const [status, setStatus] = useState('open');
  const { data, isLoading, refetch } = useListCrmTasksQuery({ view: 'client', client_id: client.id, status });
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const tasks = data?.tasks || [];
  return (
    <Card
      title="Tasks"
      icon="check2-square"
      right={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 'auto' }}>
            <option value="open">Open</option><option value="done">Done</option><option value="all">All</option>
          </select>
          <button type="button" className="btn btn-sm" onClick={() => setShowNew(true)}
                  style={{ background: work.deepBlue, color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
            <i className="bi bi-plus-lg" /> New task
          </button>
        </span>
      )}
    >
      {isLoading || !lookups ? (
        <div style={{ textAlign: 'center' }}><div className="spinner-border spinner-border-sm" /></div>
      ) : (
        <TaskTable tasks={tasks} lookups={lookups} onOpen={setOpenId} showClient={false} isMobile={isMobile}
                   empty={status === 'open' ? 'No open tasks for this client.' : 'No tasks here.'} />
      )}
      <NewTaskModal show={showNew} onHide={() => setShowNew(false)} onCreated={refetch} client={client} />
      <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={refetch} />
    </Card>
  );
}
ClientTasks.propTypes = {
  client: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }).isRequired,
  isMobile: PropTypes.bool,
};
